import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Settings as SettingsIcon, MapPin, Camera, Image as ImageIcon, Plus, X, Flag, VolumeX, MoreVertical, ChevronRight, Check, Calendar as CalendarIcon, Sparkles, Lock } from 'lucide-react';
import api, { uploadMedia } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import PlanCard from '../components/PlanCard';
import VibePost from '../components/VibePost';
import ReportModal from '../components/ReportModal';
import ImageCropper from '../components/ImageCropper';
import StoryRing from '../components/StoryRing';
import UpcomingWidget from '../components/UpcomingWidget';
import FollowButton from '../components/FollowButton';
import ProfileCompatSection from '../components/ProfileCompatSection';
import MemoriesStrip from '../components/MemoriesStrip';
import CompatibilityMatch from '../components/CompatibilityMatch';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function TrustStatus({ status }) {
  if (!status || status === 'new_user') return null;
  const config = {
    reliable: { label: 'Reliable', bg: 'bg-[#111]/[0.06]', text: 'text-[#111111]' },
    trusted: { label: 'Trusted', bg: 'bg-[#3F5200]/10', text: 'text-[#3F5200]' },
    favorite: { label: 'Favorite', bg: 'bg-[#B6E600]/15', text: 'text-[#3F5200]' },
  };
  const c = config[status] || config.reliable;
  return (
    <span data-testid="trust-status" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
}

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('plans');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showVibeForm, setShowVibeForm] = useState(false);
  const [vibeText, setVibeText] = useState('');
  const [vibeImage, setVibeImage] = useState(null);
  const [vibePreview, setVibePreview] = useState('');
  const [postingVibe, setPostingVibe] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [muting, setMuting] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperType, setCropperType] = useState(null); // 'profile' or 'cover'
  const [showStorySheet, setShowStorySheet] = useState(false);
  const [storyUploading, setStoryUploading] = useState(false);
  const [storyError, setStoryError] = useState('');
  const storyPhotoRef = useRef(null);

  const isOwnProfile = !id || id === currentUser?.id;
  const profileId = isOwnProfile ? currentUser?.id : id;

  // If BottomNav sent us here to open the vibe composer
  useEffect(() => {
    if (isOwnProfile && location.state?.openVibe) {
      setTab('vibes');
      setShowVibeForm(true);
      // clear the state so refreshes don't reopen
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (isOwnProfile && location.state?.openStory) {
      navigate('/story/camera', { replace: true });
    }
  }, [isOwnProfile, location, navigate]);

  useEffect(() => {
    if (!profileId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${profileId}`);
        setProfile(data);
        if (isOwnProfile) {
          setEditForm({
            display_name: data.display_name || '',
            bio: data.bio || '',
            city: data.city || '',
            interests: (data.interests || []).join(', ')
          });
        }
      } catch { navigate('/'); }
      setLoading(false);
    };
    fetch();
  }, [profileId, isOwnProfile, navigate]);

  const handleSaveProfile = async () => {
    try {
      const { data } = await api.put('/auth/me', {
        display_name: editForm.display_name,
        bio: editForm.bio,
        city: editForm.city,
        interests: editForm.interests.split(',').map(s => s.trim()).filter(Boolean)
      });
      setProfile(p => ({ ...p, ...data.user }));
      updateUser(data.user);
      setEditing(false);
    } catch {}
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setCropperImage(url);
    setCropperType('profile');
    e.target.value = '';
  };

  const handleCropSave = async (croppedFile) => {
    try {
      const url = (await uploadMedia(croppedFile)).url;
      const { data } = await api.put('/auth/me', { profile_photo: url });
      setProfile(p => ({ ...p, profile_photo: url }));
      updateUser(data.user);
    } catch (err) {
      console.error('Upload error:', err);
    }
    setCropperImage(null);
    setCropperType(null);
  };

  const handlePostVibe = async () => {
    if (!vibeText.trim()) return;
    setPostingVibe(true);
    try {
      let imageUrl = '';
      if (vibeImage) {
        try { imageUrl = (await uploadMedia(vibeImage)).url; } catch {}
      }
      const { data } = await api.post('/vibes', {
        text: vibeText,
        image: imageUrl,
        hashtags: []
      });
      setProfile(p => ({ ...p, vibes: [data, ...(p.vibes || [])] }));
      setVibeText('');
      setVibeImage(null);
      setVibePreview('');
      setShowVibeForm(false);
    } catch {}
    setPostingVibe(false);
  };

  const handleDeleteVibe = (vibeId) => {
    setProfile(p => ({ ...p, vibes: (p.vibes || []).filter(v => v.id !== vibeId) }));
  };

  const handleToggleMute = async () => {
    if (muting) return;
    setMuting(true);
    try {
      if (profile.is_muted) {
        await api.delete(`/users/${profileId}/mute`);
        setProfile(p => ({ ...p, is_muted: false }));
      } else {
        await api.post(`/users/${profileId}/mute`);
        setProfile(p => ({ ...p, is_muted: true }));
      }
    } catch {}
    setMuting(false);
    setShowActions(false);
  };

  const [startingDM, setStartingDM] = useState(false);
  const handleStartDM = async () => {
    if (startingDM || !profileId) return;
    setStartingDM(true);
    try {
      const { data } = await api.post('/dm/threads', { other_user_id: profileId });
      navigate(`/dm/${data.id}`);
    } catch (err) {
      alert(err.response?.data?.detail || "Can't message this user");
    }
    setStartingDM(false);
  };

  // story creation now routes through /story/camera (no sheet)

  if (loading) {
    return (
      <div className="content-area px-4 pt-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="skeleton w-20 h-20 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-5 w-32" />
            <div className="skeleton h-4 w-20" />
          </div>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const tabs = [
    { key: 'plans', label: 'Plans' },
    { key: 'vibes', label: 'Vibes' },
    { key: 'about', label: 'Profile' }
  ];

  return (
    <div className="content-area animate-fade-in" data-testid="profile-page">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        {!isOwnProfile ? (
          <button data-testid="profile-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={24} className="text-[#111111]" />
          </button>
        ) : <div />}
        {isOwnProfile && (
          <div className="flex items-center gap-3">
            <button
              data-testid="edit-profile-btn"
              onClick={() => navigate('/edit-profile')}
              className="text-xs text-[#6B6E73] font-medium bg-[#F1F0EA] px-3 py-1.5 rounded-full border border-black/[0.06]"
            >
              Edit
            </button>
            <button
              data-testid="profile-calendar-btn"
              onClick={() => navigate('/calendar')}
              className="text-[#6B6E73]"
              aria-label="My calendar"
            >
              <CalendarIcon size={20} />
            </button>
            <button
              data-testid="settings-btn"
              onClick={() => navigate('/settings')}
              className="text-[#6B6E73]"
              aria-label="Settings"
            >
              <SettingsIcon size={20} />
            </button>
          </div>
        )}
        {!isOwnProfile && (
          <button
            data-testid="profile-actions-btn"
            onClick={() => setShowActions(true)}
            className="text-[#6B6E73] p-1 -mr-1"
            aria-label="More options"
          >
            <MoreVertical size={22} />
          </button>
        )}
      </div>

      {/* Profile Header */}
      <div className="px-4 mb-5">
        <div className="flex items-center gap-4">
          <div className="relative">
            <StoryRing
              userId={profile.id}
              displayName={profile.display_name}
              profilePhoto={profile.profile_photo}
              size={80}
              hasActiveStory={!!profile.has_active_story}
              hasUnviewedStory={!!profile.has_unviewed_story}
              testId="profile-story-ring"
            />
            {isOwnProfile && (
              <button
                type="button"
                data-testid="add-story-btn"
                onClick={() => navigate('/story/camera')}
                className="absolute -bottom-1 -right-1 bg-[#B6E600] rounded-full p-1.5 active:scale-90 transition-transform shadow-sm"
                aria-label="Add to story"
              >
                <Plus size={14} strokeWidth={3} className="text-black" />
              </button>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2 className="text-xl font-bold text-[#111111]">{profile.display_name}</h2>
              {(profile.badges_computed || []).some(b => b.slug === 'verified_host') && (
                <span data-testid="verified-host-badge" title="Verified Host" className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#B6E600] text-[#111] text-[10px] font-extrabold">
                  ✓
                </span>
              )}
            </div>
            <p className="text-sm text-[#9B9EA3]">@{profile.username}</p>
            {currentUser?.role === 'admin' && profile?.is_demo && (
              <span data-testid="demo-badge" className="inline-block mt-1 text-[9px] uppercase tracking-wider bg-[#FFF3CD] text-[#7A4F00] px-1.5 py-0.5 rounded-full font-extrabold">DEMO</span>
            )}
            {profile.reputation?.trust_status && <TrustStatus status={profile.reputation.trust_status} />}
            {profile.joined_since && (
              <p data-testid="joined-since" className="text-[11px] text-[#9B9EA3] mt-1">{profile.joined_since}</p>
            )}
          </div>
        </div>

        {/* Badge chips row */}
        {(profile.badges_computed || []).length > 0 && (
          <div data-testid="badge-chips" className="flex gap-1.5 mt-3 flex-wrap">
            {profile.badges_computed.map(b => (
              <span
                key={b.slug}
                data-testid={`profile-badge-${b.slug}`}
                className="text-[10px] font-extrabold bg-[#F1F0EA] text-[#3F5200] px-2 py-1 rounded-full"
              >
                {b.emoji} {b.label}
              </span>
            ))}
          </div>
        )}

        {/* Bio - always visible */}
        {profile.bio ? (
          <p className="text-sm text-[#6B6E73] mt-3 leading-relaxed">{profile.bio}</p>
        ) : isOwnProfile ? (
          <button
            onClick={() => navigate('/edit-profile')}
            className="text-sm text-[#B0B3B8] mt-3 italic active:text-[#9B9EA3] transition-colors"
          >
            Add a bio
          </button>
        ) : null}
        {profile.city && (
          <div className="flex items-center gap-1 mt-1.5 text-xs text-[#9B9EA3]">
            <MapPin size={12} /> {profile.city}
          </div>
        )}

        {/* Stats - Clickable → Full Screen Pages */}
        <div className="flex gap-4 mt-4">
          {[
            { label: 'Followers', value: profile.followers_count || 0, testId: 'stat-followers', path: 'followers' },
            { label: 'Following', value: profile.following_count || 0, testId: 'stat-following', path: 'following' },
            { label: 'Hosted', value: profile.hosted_count || 0, testId: 'stat-hosted', path: 'hosted' },
            { label: 'Attended', value: profile.attended_count || profile.joined_count || 0, testId: 'stat-attended', path: 'attended' },
          ].map(stat => (
            <button
              key={stat.label}
              data-testid={stat.testId}
              onClick={() => navigate(`/user/${profileId}/${stat.path}`)}
              className="flex-1 text-center py-2 rounded-xl active:scale-95 active:bg-black/[0.03] transition-all"
            >
              <p className="text-lg font-bold text-[#111111]">{stat.value}</p>
              <p className="text-[10px] text-[#9B9EA3] uppercase tracking-wider">{stat.label}</p>
            </button>
          ))}
        </div>

        {/* Mutual followers + Usually joins */}
        {!isOwnProfile && profile.mutual_followers > 0 && (
          <p className="text-xs text-[#6B6E73] mt-2">
            <span className="text-[#111111] font-medium">{profile.mutual_followers}</span> mutual follower{profile.mutual_followers !== 1 ? 's' : ''}
          </p>
        )}
        {profile.usually_joins?.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#9B9EA3]">
            <span>Usually joins</span>
            {profile.usually_joins.map(c => (
              <span key={c} className="bg-white/[0.04] px-2 py-0.5 rounded-full text-[#6B6E73] capitalize">{c}</span>
            ))}
          </div>
        )}

        {/* Primary CTAs — Follow (lime brand primary) + Message (white
            secondary). Placed immediately after Stats so the social
            action sits above the fold on every profile. */}
        {!isOwnProfile && (
          <div className="mt-4 flex gap-2" data-testid="profile-cta-row">
            <FollowButton
              user={profile}
              size="md"
              variant="block"
              onChange={(newStatus) => {
                setProfile(p => ({
                  ...p,
                  is_following: newStatus,
                  followers_count:
                    newStatus === 'active'
                      ? (p.followers_count || 0) + (p.is_following === 'active' ? 0 : 1)
                      : Math.max(0, (p.followers_count || 0) - (p.is_following === 'active' ? 1 : 0)),
                }));
              }}
            />
            <button
              data-testid="message-profile-btn"
              onClick={handleStartDM}
              disabled={startingDM}
              className="flex-1 bg-white text-[#111111] font-bold rounded-full py-2.5 text-sm border border-black/[0.12] active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {startingDM ? 'Opening…' : 'Message'}
            </button>
          </div>
        )}

        {/* Interests */}
        {!editing && profile.interests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid="profile-interests">
            {profile.interests.map(i => (
              <span key={i} className="bg-[#3F5200]/10 text-[#3F5200] px-3 py-1 rounded-full text-[11px] font-semibold border border-[#6B8A00]/30">
                {i}
              </span>
            ))}
          </div>
        )}

        {/* Q39 — Compatibility profile (Quick Snapshot + Conversation Starters + Reputation card) */}
        {!editing && (
          <ProfileCompatSection
            compat={profile.compat}
            displayName={profile.display_name}
            isOwn={isOwnProfile}
          />
        )}

        {/* Q39 Phase C — Compatibility match (only on other people's profiles) */}
        {!editing && !isOwnProfile && (
          <CompatibilityMatch
            targetUserId={profile.id}
            isOwn={isOwnProfile}
            displayName={profile.display_name}
          />
        )}

        {/* Q39 Phase B — Memories strip (auto-pulled from hosted/attended plans) */}
        {!editing && (
          <MemoriesStrip
            memories={profile.compat?.memories || []}
            isOwn={isOwnProfile}
            displayName={profile.display_name}
          />
        )}

        {/* Reputation v2 — moved BELOW the social actions and interests so
            the social-first hierarchy reads correctly. Gamification supports
            the relationship, it doesn't gate it. */}
        {profile.reputation_v2 && (
          <div data-testid="reputation-section" className="mt-3 bg-white rounded-2xl border border-black/[0.04] p-4">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl flex-shrink-0">{profile.reputation_v2.level.emoji}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#6B6E73]">Level {profile.reputation_v2.level.level}</p>
                <p className="text-base font-extrabold text-[#111] truncate" data-testid="level-name">{profile.reputation_v2.level.name}</p>
              </div>
              <p className="text-lg font-extrabold text-[#111] tabular-nums" data-testid="level-points">{profile.reputation_v2.points}</p>
            </div>
            {profile.reputation_v2.level.next ? (
              <>
                <div className="w-full h-2 bg-[#F1F0EA] rounded-full overflow-hidden">
                  <div data-testid="level-progress" className="h-full bg-[#B6E600] rounded-full transition-all" style={{ width: `${profile.reputation_v2.level.pct_to_next}%` }} />
                </div>
                <p className="text-[11px] text-[#6B6E73] mt-1.5">
                  <strong>{profile.reputation_v2.level.points_to_next}</strong> pts until <strong>{profile.reputation_v2.level.next.name}</strong> {profile.reputation_v2.level.next.emoji}
                </p>
                {profile.reputation_v2.level.next.unlock && (
                  <div data-testid="level-unlock" className="mt-2 flex items-start gap-1.5 bg-[#B6E600]/10 border border-[#B6E600]/30 rounded-lg px-2.5 py-1.5">
                    <span className="text-[12px] flex-shrink-0">🎁</span>
                    <p className="text-[11px] text-[#3F5200] leading-snug">
                      <strong>Unlocks:</strong> {profile.reputation_v2.level.next.unlock}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="text-xs font-extrabold text-[#3F5200]">👑 Max level reached</p>
                {profile.reputation_v2.level.unlock && (
                  <p data-testid="level-unlock" className="text-[11px] text-[#6B6E73] mt-1">
                    <strong>Active perks:</strong> {profile.reputation_v2.level.unlock}
                  </p>
                )}
              </>
            )}

            {/* Single rank chip — tap to explore category leaderboards */}
            {profile.reputation_v2.rank && (
              <button
                onClick={() => navigate(`/leaderboard?city=${encodeURIComponent(profile.city || '')}&category=overall`)}
                data-testid="rank-chip"
                className="w-full mt-3 flex items-center justify-between bg-[#F1F0EA] rounded-2xl px-3 py-2.5 active:scale-[0.99]"
              >
                <span className="text-xs font-extrabold text-[#111]">
                  🏆 {profile.reputation_v2.rank.label || `Ranked #${profile.reputation_v2.rank.rank} of ${profile.reputation_v2.rank.total}`} in {profile.city || 'your city'}
                </span>
                <ChevronRight size={14} className="text-[#9B9EA3]" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Locked private-account state — replaces tabs for non-followers */}
      {profile.locked ? (
        <div className="px-6 py-10 text-center" data-testid="profile-locked-state">
          <div className="w-14 h-14 rounded-full bg-[#F1F0EA] flex items-center justify-center mx-auto mb-3">
            <Lock size={20} className="text-[#6B6E73]" strokeWidth={2.4} />
          </div>
          <p className="text-base font-extrabold text-[#111] mb-1">This account is private</p>
          <p className="text-sm text-[#6B6E73] leading-relaxed max-w-xs mx-auto">
            Follow {profile.display_name || `@${profile.username}`} to see their stories, vibes, and plans.
          </p>
        </div>
      ) : (
        <>
      {isOwnProfile && <UpcomingWidget limit={3} title="Your upcoming" />}
      {/* Tabs */}
      <div className="px-4 flex gap-1 border-b border-black/[0.06] mb-4">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`profile-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-3 text-xs font-medium text-center transition-colors relative ${
              tab === t.key ? 'text-[#111111] tab-active' : 'text-[#9B9EA3]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="px-4 pb-4">
        {tab === 'plans' && (
          <div className="space-y-3 stagger-children">
            {(profile.plans || []).length === 0 ? (
              <div className="text-center py-12 px-6" data-testid="profile-plans-empty">
                <div className="w-14 h-14 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-3">
                  <CalendarIcon size={22} className="text-[#3F5200]" />
                </div>
                <p className="text-sm font-extrabold text-[#111] mb-1">
                  {isOwnProfile ? 'No plans yet' : `${profile.display_name?.split(' ')[0] || 'They'} hasn't hosted yet`}
                </p>
                <p className="text-xs text-[#6B6E73] leading-relaxed mb-3 max-w-[260px] mx-auto">
                  {isOwnProfile
                    ? 'Host your first plan and start meeting people who match your vibe ✨'
                    : 'Check back soon or explore plans they might join.'}
                </p>
                {isOwnProfile && (
                  <button
                    data-testid="profile-plans-empty-cta"
                    onClick={() => navigate('/create')}
                    className="px-5 py-2.5 rounded-full bg-[#B6E600] text-[#111] text-xs font-extrabold active:scale-95"
                  >
                    Host a plan
                  </button>
                )}
              </div>
            ) : (
              profile.plans.map(p => (
                <div
                  key={p.id}
                  data-testid={`profile-plan-${p.id}`}
                  onClick={() => navigate(`/plan/${p.id}`)}
                  className="bg-[#FFFFFF] rounded-2xl p-3 border border-black/[0.06] cursor-pointer animate-fade-in active:scale-[0.99] transition-transform"
                >
                  <div className="flex gap-3">
                    {p.cover_image && (
                      <img loading="lazy" decoding="async" src={p.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                    )}
                    <div>
                      <h4 className="font-semibold text-sm text-[#111111]">{p.title}</h4>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">{p.date} {p.start_time}</p>
                      <p className="text-xs text-[#6B6E73] mt-0.5">{p.venue || p.city}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'vibes' && (
          <div className="space-y-3">
            {isOwnProfile && (
              <div className="mb-4">
                {showVibeForm ? (
                  <div className="bg-[#FFFFFF] rounded-2xl p-4 border border-black/[0.06] space-y-3" data-testid="vibe-form">
                    <textarea
                      data-testid="vibe-text-input"
                      value={vibeText}
                      onChange={e => setVibeText(e.target.value)}
                      placeholder="Share a vibe..."
                      rows={3}
                      className="w-full bg-[#F1F0EA] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 resize-none placeholder-[#B0B3B8]"
                    />
                    {vibePreview && (
                      <div className="relative">
                        <img loading="lazy" decoding="async" src={vibePreview} alt="" className="w-full h-[140px] object-cover rounded-xl" />
                        <button onClick={() => { setVibeImage(null); setVibePreview(''); }} className="absolute top-2 right-2 bg-white/70 rounded-full p-1">
                          <X size={14} className="text-[#111111]" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <label className="cursor-pointer">
                        <Camera size={20} className="text-[#9B9EA3]" />
                        <input type="file" accept="image/*" className="hidden" onChange={e => {
                          const f = e.target.files?.[0];
                          if (f) { setVibeImage(f); setVibePreview(URL.createObjectURL(f)); }
                        }} />
                      </label>
                      <div className="flex gap-2">
                        <button onClick={() => { setShowVibeForm(false); setVibeText(''); }} className="text-xs text-[#9B9EA3] px-3 py-1.5">Cancel</button>
                        <button
                          data-testid="post-vibe-btn"
                          onClick={handlePostVibe}
                          disabled={postingVibe || !vibeText.trim()}
                          className="bg-[#111111] text-white text-xs font-bold px-4 py-1.5 rounded-full active:scale-95 disabled:opacity-50"
                        >
                          {postingVibe ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid="new-vibe-btn"
                    onClick={() => setShowVibeForm(true)}
                    className="w-full bg-[#FFFFFF] rounded-2xl p-4 border border-black/[0.06] text-sm text-[#9B9EA3] text-left"
                  >
                    Share a vibe...
                  </button>
                )}
              </div>
            )}
            <div className="space-y-3 stagger-children">
              {(profile.vibes || []).length === 0 ? (
                <div className="text-center py-12 px-6" data-testid="profile-vibes-empty">
                  <div className="w-14 h-14 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-3">
                    <Sparkles size={22} className="text-[#3F5200]" />
                  </div>
                  <p className="text-sm font-extrabold text-[#111] mb-1">
                    {isOwnProfile ? 'No vibes yet' : 'Quiet over here'}
                  </p>
                  <p className="text-xs text-[#6B6E73] leading-relaxed mb-3 max-w-[260px] mx-auto">
                    {isOwnProfile
                      ? 'Share a quick post about what you\'re into — your future plans start here.'
                      : 'They haven\'t posted yet. Follow to see when they do.'}
                  </p>
                  {isOwnProfile && (
                    <button
                      data-testid="profile-vibes-empty-cta"
                      onClick={() => setShowVibeForm(true)}
                      className="px-5 py-2.5 rounded-full bg-[#B6E600] text-[#111] text-xs font-extrabold active:scale-95"
                    >
                      Post your first vibe
                    </button>
                  )}
                </div>
              ) : (
                profile.vibes.map(v => (
                  <VibePost key={v.id} vibe={v} onDelete={handleDeleteVibe} />
                ))
              )}
            </div>
          </div>
        )}

        {tab === 'about' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-[#FFFFFF] rounded-2xl p-4 border border-black/[0.06] space-y-3">
              {profile.city && (
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-[#B6E600]" />
                  <span className="text-sm text-[#111111]">{profile.city}</span>
                </div>
              )}
              {profile.interests?.length > 0 && (
                <div>
                  <p className="text-xs text-[#9B9EA3] uppercase tracking-wider mb-2">Interests</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.interests.map(i => (
                      <span key={i} className="bg-[#3F5200]/10 text-[#3F5200] px-3 py-1 rounded-full text-[11px] font-semibold border border-[#6B8A00]/30">
                        {i}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {profile.usually_joins?.length > 0 && (
                <div>
                  <p className="text-xs text-[#9B9EA3] uppercase tracking-wider mb-2">Usually Joins</p>
                  <div className="flex flex-wrap gap-1.5">
                    {profile.usually_joins.map(c => (
                      <span key={c} className="bg-white/[0.04] text-[#6B6E73] px-3 py-1 rounded-full text-[11px] font-medium capitalize">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Image Cropper */}
      {cropperImage && (
        <ImageCropper
          imageSrc={cropperImage}
          onSave={handleCropSave}
          onCancel={() => { setCropperImage(null); setCropperType(null); }}
          aspect={cropperType === 'profile' ? 1 : 16 / 9}
          circular={cropperType === 'profile'}
        />
      )}

      {/* Report Modal */}
      {showReport && <ReportModal type="user" targetId={profileId} onClose={() => setShowReport(false)} />}

      {/* Profile Actions Bottom Sheet (other users only) */}
      {showActions && !isOwnProfile && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setShowActions(false)}
          data-testid="profile-actions-overlay"
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up"
            onClick={e => e.stopPropagation()}
            data-testid="profile-actions-sheet"
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-sm font-bold text-[#111111]">@{profile.username}</p>
            </div>
            <div className="px-3 pt-2">
              <button
                data-testid="action-mute-btn"
                onClick={handleToggleMute}
                disabled={muting}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left disabled:opacity-60"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
                  {profile.is_muted ? <Check size={18} className="text-[#3F5200]" /> : <VolumeX size={18} className="text-[#111111]" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#111111]">
                    {profile.is_muted ? 'Unmute User' : 'Mute User'}
                  </p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">
                    {profile.is_muted ? 'Show their vibes and plans again' : 'Hide their vibes and plans from your feed'}
                  </p>
                </div>
              </button>
              <button
                data-testid="action-report-btn"
                onClick={() => { setShowActions(false); setShowReport(true); }}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                  <Flag size={18} className="text-[#FF3B30]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#FF3B30]">Report User</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">Flag behavior that breaks our guidelines</p>
                </div>
              </button>
            </div>
            <div className="px-5 pt-4">
              <button
                onClick={() => setShowActions(false)}
                className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111] active:scale-[0.99] transition-transform"
                data-testid="action-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
